// src/pages/CareersPage.js

import React, { useState, useEffect } from 'react';
import { getCareers, deleteCareer } from '../services/careerService';
import {
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Container,
  Box,
  TableContainer,
  Paper,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Grid,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import AdminEmptyState from '../components/AdminEmptyState';
import AdminPageHeader from '../components/AdminPageHeader';
import AdminPagination from '../components/AdminPagination';
import AdminSection from '../components/AdminSection';
import useAdminPagination from '../hooks/useAdminPagination';

function CareersPage() {
  const [careers, setCareers] = useState([]);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Detect if the screen is small (mobile)
  const {
    page,
    rowsPerPage,
    count,
    paginatedItems: paginatedCareers,
    handlePageChange,
    handleRowsPerPageChange,
  } = useAdminPagination(careers, 10);

  useEffect(() => {
    fetchCareers();
  }, []);

  const fetchCareers = async () => {
    try {
      const data = await getCareers();
      setCareers(data);
    } catch (error) {
      console.error('Error fetching careers:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this career?')) {
      try {
        await deleteCareer(id);
        fetchCareers(); // Refresh the list after deletion
      } catch (error) {
        console.error('Error deleting career:', error);
      }
    }
  };

  return (
    <DashboardLayout>
      <Container>
        <Box mt={4}>
          <AdminPageHeader
            eyebrow="Operations"
            title="Careers"
            description="Manage open roles and keep the careers page current."
            action={<Button variant="contained" onClick={() => navigate('/careers/create')}>Add Career</Button>}
          />
          <motion.div whileHover={{ scale: 1.05 }} transition={{ duration: 0.3 }}>
            <Box sx={{ display: 'none' }} />
          </motion.div>

          {isMobile ? (
            // For mobile screens, display careers as cards
            <Grid container spacing={2}>
              {paginatedCareers.map((career) => (
                <Grid item xs={12} key={career.careerId}>
                  <AdminSection sx={{ p: 0 }}>
                  <Card variant="outlined" sx={{ border: 0, boxShadow: 'none' }}>
                    <CardContent>
                      <Typography variant="h6">{career.position}</Typography>
                      <Typography>{career.description}</Typography>
                    </CardContent>
                    <CardActions>
                      <IconButton
                        color="primary"
                        onClick={() => navigate(`/careers/edit/${career.careerId}`)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(career.careerId)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </CardActions>
                  </Card>
                  </AdminSection>
                </Grid>
              ))}
              {!careers.length && (
                <Grid item xs={12}>
                  <AdminSection>
                    <AdminEmptyState title="No careers yet" description="Add the first job opening to publish it on the careers page." />
                  </AdminSection>
                </Grid>
              )}
            </Grid>
          ) : (
            // For larger screens, display careers in a table
            <AdminSection sx={{ p: 0, overflow: 'hidden' }}>
            <TableContainer component={Paper} sx={{ boxShadow: 0, borderRadius: 0 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Position</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedCareers.map((career) => (
                    <TableRow key={career.careerId}>
                      <TableCell>{career.position}</TableCell>
                      <TableCell>{career.description}</TableCell>
                      <TableCell align="right">
                        <motion.div whileHover={{ scale: 1.05 }} transition={{ duration: 0.3 }}>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => navigate(`/careers/edit/${career.careerId}`)}
                            sx={{ marginRight: 1 }}
                          >
                            Edit
                          </Button>
                        </motion.div>
                        <motion.div whileHover={{ scale: 1.05 }} transition={{ duration: 0.3 }}>
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => handleDelete(career.careerId)}
                          >
                            Delete
                          </Button>
                        </motion.div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!careers.length && (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <AdminEmptyState title="No careers yet" description="Add the first job opening to publish it on the careers page." />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <AdminPagination
              count={count}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
            </AdminSection>
          )}
          {isMobile ? (
            <AdminPagination
              count={count}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          ) : null}
        </Box>
      </Container>
    </DashboardLayout>
  );
}

export default CareersPage;
